import React, { useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ResponsiveSelect from "../components/forms/shared/ResponsiveSelect.jsx";
import api from "../constants/api";
import { showError, showSuccess } from "../utils/toast.js";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { DateOnlyField, Label } from "../components/ui/DatePickers.jsx";
import ModuleToggle from "../components/forms/shared/ModuleToggle.jsx";
import ModuleBlock from "../components/forms/modules/ModuleBlock.jsx";
import { getDefaults } from "../../shared/config/formModules.js";
import { getRsStyles } from "../components/forms/shared/SelectStyles.js";
import { Input, Textarea } from "../components/forms/shared/FormAtoms.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import KundeSelectAsync from "../components/Kunden/KundeSelectAsync.jsx";
import { detailPageHeaderNavSecondaryShellClassName, successButtonLoadingClassName } from "../design/tokens.js";
const TYPEN = ["IT", "ALARM", "VIDEO", "ELEKTRO", "PV", "LADESTATION"];
const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const SERVICE_MODULE_DEFAULTS = getDefaults("service");
export default function WartungsvertragErstellen() {
  const {
    id
  } = useParams();
  const vertragId = Number(id);
  const isEditMode = Number.isFinite(vertragId) && vertragId > 0;
    const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [touched, setTouched] = useState(false);
  const [objekte, setObjekte] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    titel: "",
    kunde_id: "",
    objekt_id: "",
    typ: "ELEKTRO",
    status: "AKTIV",
    vertragsbeginn: todayYmd(),
    laufzeit_monate: 12,
    notify_days_before: 14,
    auto_renew: false,
    projektleiter_user_id: "",
    auftraggeber_user_id: "",
    kuendigungstermin: "",
    agb_version_id: "",
    template_key: "default",
    notizen_intern: "",
    verwaltung_text: "",
    abweichende_adresse: "",
    adresse_strasse: "",
    adresse_plz: "",
    adresse_ort: "",
    kontakt_vor_ort_name: "",
    kontakt_vor_ort_tel: "",
    kontakt_vor_ort_email: "",
    rechnung_owner_type: "BUERO",
    rechnung_owner_user_id: "",
    enabled_modules: [],
    modules: {}
  });
  const formBaseId = useId();
  const fieldId = name => `wartungsvertrag-${formBaseId}-${name}`;
  useEffect(() => {
    (async () => {
      try {
        const u = await api.get("/service/users/options");
        setUsers(Array.isArray(u?.data) ? u.data : []);
      } catch {
        showError("Benutzerliste konnte nicht geladen werden.");
      }
    })();
  }, []);
  useEffect(() => {
    const prefillKunde = Number(location.state?.prefill?.kunde_id ?? new URLSearchParams(location.search).get("kunde_id")) || null;
    const prefillObjekt = Number(location.state?.prefill?.objekt_id ?? new URLSearchParams(location.search).get("objekt_id")) || null;
    if (!isEditMode && prefillKunde) {
      setForm(s => ({
        ...s,
        kunde_id: String(prefillKunde),
        objekt_id: prefillObjekt ? String(prefillObjekt) : s.objekt_id
      }));
    }
  }, [location.key, location.search, location.state, isEditMode]);
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        setLoadingEdit(true);
        const {
          data
        } = await api.get(`/wartungsvertraege/${vertragId}`);
        const item = data?.data || null;
        if (!item) throw new Error("NOT_FOUND");
        const normalizedStatus = (() => {
          const sRaw = String(item.status || "").toUpperCase();
          if (sRaw === "ARCHIVIERT") return "ARCHIVIERT";
          if (sRaw === "PAUSIERT" || sRaw === "DEAKTIVIERT") return "PAUSIERT";
          return "AKTIV";
        })();
        setForm(s => ({
          ...s,
          titel: item.titel || `Wartungsvertrag ${item.typ || s.typ}`,
          kunde_id: item.kunde_id ? String(item.kunde_id) : "",
          objekt_id: item.objekt_id ? String(item.objekt_id) : "",
          typ: item.typ || s.typ,
          status: normalizedStatus,
          vertragsbeginn: item.vertragsbeginn ? String(item.vertragsbeginn).slice(0, 10) : s.vertragsbeginn,
          laufzeit_monate: item.laufzeit_monate ?? s.laufzeit_monate,
          notify_days_before: Number(item.notify_days_before ?? s.notify_days_before),
          auto_renew: !!item.auto_renew,
          projektleiter_user_id: item.projektleiter_user_id ? String(item.projektleiter_user_id) : "",
          auftraggeber_user_id: item.auftraggeber_user_id ? String(item.auftraggeber_user_id) : "",
          kuendigungstermin: item.kuendigungstermin ? String(item.kuendigungstermin).slice(0, 10) : "",
          agb_version_id: item.agb_version_id ? String(item.agb_version_id) : "",
          template_key: item.template_key || "default",
          notizen_intern: item.notizen_intern || "",
          verwaltung_text: item.verwaltung_text || "",
          abweichende_adresse: item.abweichende_adresse || "",
          adresse_strasse: item.adresse_strasse || "",
          adresse_plz: item.adresse_plz || "",
          adresse_ort: item.adresse_ort || "",
          kontakt_vor_ort_name: item.kontakt_vor_ort_name || "",
          kontakt_vor_ort_tel: item.kontakt_vor_ort_tel || "",
          kontakt_vor_ort_email: item.kontakt_vor_ort_email || "",
          rechnung_owner_type: item.rechnung_owner_type || "BUERO",
          rechnung_owner_user_id: item.rechnung_owner_user_id ? String(item.rechnung_owner_user_id) : "",
          enabled_modules: Array.isArray(item.enabled_modules) ? item.enabled_modules : [],
          modules: item.modules && typeof item.modules === "object" ? item.modules : {}
        }));
      } catch {
        showError("Wartungsvertrag konnte nicht geladen werden.");
        navigate("/wartungsvertraege");
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [isEditMode, vertragId, navigate]);
  useEffect(() => {
    const kid = Number(form.kunde_id || 0);
    if (!kid) {
      setObjekte([]);
      return;
    }
    (async () => {
      try {
        const {
          data
        } = await api.get(`/objekte/select/${kid}`);
        setObjekte(Array.isArray(data) ? data : []);
      } catch {
        setObjekte([]);
      }
    })();
  }, [form.kunde_id]);
  async function onSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (!form.kunde_id || !form.objekt_id || !form.typ || !form.vertragsbeginn) {
      showError("Bitte Pflichtfelder ausfüllen.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        titel: form.titel || null,
        kunde_id: Number(form.kunde_id),
        objekt_id: Number(form.objekt_id),
        typ: form.typ,
        vertragsbeginn: form.vertragsbeginn,
        projektleiter_user_id: form.projektleiter_user_id ? Number(form.projektleiter_user_id) : null,
        auftraggeber_user_id: form.auftraggeber_user_id ? Number(form.auftraggeber_user_id) : null,
        kuendigungstermin: form.kuendigungstermin || null,
        template_key: form.template_key || "default",
        notizen_intern: form.notizen_intern || null,
        verwaltung_text: form.verwaltung_text || null,
        abweichende_adresse: form.abweichende_adresse || null,
        adresse_strasse: form.adresse_strasse || null,
        adresse_plz: form.adresse_plz || null,
        adresse_ort: form.adresse_ort || null,
        kontakt_vor_ort_name: form.kontakt_vor_ort_name || null,
        kontakt_vor_ort_tel: form.kontakt_vor_ort_tel || null,
        kontakt_vor_ort_email: form.kontakt_vor_ort_email || null,
        rechnung_owner_type: form.rechnung_owner_type || "BUERO",
        rechnung_owner_user_id: null,
        enabled_modules: Array.isArray(form.enabled_modules) ? form.enabled_modules : [],
        modules: form.modules && typeof form.modules === "object" ? form.modules : {}
      };
      if (!isEditMode) {
        payload.status = "AKTIV";
        payload.laufzeit_monate = Number(form.laufzeit_monate || 12);
        payload.notify_days_before = Number(form.notify_days_before || 14);
        payload.auto_renew = !!form.auto_renew;
        payload.agb_version_id = form.agb_version_id ? Number(form.agb_version_id) : null;
        payload.assigned_user_ids = [];
      }
      if (isEditMode) {
        await api.put(`/wartungsvertraege/${vertragId}`, payload);
        showSuccess("Wartungsvertrag aktualisiert.");
        navigate(`/wartungsvertraege/${vertragId}`);
      } else {
        const {
          data
        } = await api.post("/wartungsvertraege", payload);
        showSuccess("Wartungsvertrag erstellt.");
        navigate(`/wartungsvertraege/${data?.id}`);
      }
    } catch {
      showError(isEditMode ? "Speichern fehlgeschlagen." : "Erstellen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }
  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const card = "app-frame";
  const displayName = user?.name || [user?.vorname, user?.nachname].filter(Boolean).join(" ").trim() || user?.email || "";
  const availableModules = SERVICE_MODULE_DEFAULTS.available;
  const rsStyles = useMemo(() => getRsStyles(true), []);
  const menuPortalTarget = useMemo(() => typeof document !== "undefined" ? document.body : undefined, []);
  const objekteOpts = useMemo(() => objekte.map(o => ({
    value: Number(o.id),
    label: o.name || o.adresse || `Objekt #${o.id}`
  })), [objekte]);
  const userOpts = useMemo(() => users.map(u => ({
    value: Number(u.id),
    label: u.name
  })), [users]);
  const typOpts = useMemo(() => TYPEN.map(t => ({
    value: t,
    label: t
  })), []);
  const moduleValues = useMemo(() => ({
    verwaltung: {
      verwaltung_text: form.verwaltung_text || ""
    },
    einsatzort: {
      abweichende_adresse: form.abweichende_adresse || ""
    },
    kontakt_vor_ort: {
      ...(form.modules?.kontakt_vor_ort || {}),
      name: form.modules?.kontakt_vor_ort?.name ?? form.kontakt_vor_ort_name ?? "",
      telefon: form.modules?.kontakt_vor_ort?.telefon ?? form.kontakt_vor_ort_tel ?? "",
      email: form.modules?.kontakt_vor_ort?.email ?? form.kontakt_vor_ort_email ?? "",
      funktion: form.modules?.kontakt_vor_ort?.funktion ?? ""
    },
    rechnungsadresse: {
      ...(form.modules?.rechnungsadresse || {}),
      strasse: form.modules?.rechnungsadresse?.strasse ?? form.adresse_strasse ?? "",
      plz: form.modules?.rechnungsadresse?.plz ?? form.adresse_plz ?? "",
      ort: form.modules?.rechnungsadresse?.ort ?? form.adresse_ort ?? ""
    },
    interne_referenz: {
      ...(form.modules?.interne_referenz || {})
    },
    zusatzinfos: {
      ...(form.modules?.zusatzinfos || {})
    }
  }), [form]);
  const setEnabledModules = nextEnabled => {
    setForm(prev => ({
      ...prev,
      enabled_modules: Array.isArray(nextEnabled) ? nextEnabled : prev.enabled_modules
    }));
  };
  const onModuleFieldChange = (moduleId, fieldName, value) => {
    setForm(prev => {
      const next = {
        ...prev
      };
      if (moduleId === "verwaltung" && fieldName === "verwaltung_text") next.verwaltung_text = value;
      if (moduleId === "einsatzort" && fieldName === "abweichende_adresse") next.abweichende_adresse = value;
      if (moduleId === "kontakt_vor_ort") {
        if (fieldName === "name") next.kontakt_vor_ort_name = value;
        if (fieldName === "telefon") next.kontakt_vor_ort_tel = value;
        if (fieldName === "email") next.kontakt_vor_ort_email = value;
      }
      if (moduleId === "rechnungsadresse") {
        if (fieldName === "strasse") next.adresse_strasse = value;
        if (fieldName === "plz") next.adresse_plz = value;
        if (fieldName === "ort") next.adresse_ort = value;
      }
      next.modules = {
        ...(prev.modules || {}),
        [moduleId]: {
          ...(prev.modules?.[moduleId] || {}),
          [fieldName]: value
        }
      };
      return next;
    });
  };
  return <div className={grad}>
      <div className="mx-auto w-full px-3 sm:px-4 py-4 sm:py-6">
        <div className="mx-auto max-w-screen-sm md:max-w-3xl">
          <section className={`rounded-2xl border shadow-xl ${card}`}>
            <form onSubmit={onSubmit} className={`no-anchor grid grid-cols-1 md:grid-cols-2 gap-4 px-4 sm:px-5 py-4 sm:py-5 ${"text-blue-50"}`}>
              <div className="md:col-span-2 mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h2 className="text-xl font-semibold">{isEditMode ? `Wartungsvertrag bearbeiten #${vertragId}` : "Neuer Wartungsvertrag"}</h2>
                  {displayName ? <div className="text-xs text-blue-200/70 mt-1">Ersteller: {displayName}</div> : null}
                </div>
              </div>

              {loadingEdit ? <div className="text-sm opacity-80 py-2">Lade Vertrag...</div> : null}

              <div className="md:col-span-2">
                <Label htmlFor={fieldId("titel")}>Titel</Label>
                <Input id={fieldId("titel")} type="text" value={form.titel} onChange={e => setForm(s => ({
                ...s,
                titel: e.target.value
              }))} placeholder="z. B. Wartungsvertrag ELEKTRO" autoComplete="off" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor={fieldId("beschreibung")}>Beschreibung</Label>
                <Textarea id={fieldId("beschreibung")} rows={3} value={form.notizen_intern} onChange={e => setForm(s => ({
                ...s,
                notizen_intern: e.target.value
              }))} placeholder="Beschreibung für Wartungsinformation" />
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={fieldId("kunde-id")}>Kunde *</Label>
                    <KundeSelectAsync inputId={fieldId("kunde-id")} value={form.kunde_id ? Number(form.kunde_id) : null} onChange={id => setForm(s => ({
                    ...s,
                    kunde_id: id ? String(id) : "",
                    objekt_id: ""
                  }))} placeholder="Kunde suchen…" />
                  </div>
                  <div>
                    <Label htmlFor={fieldId("objekt-id")}>Objekt / Liegenschaft *</Label>
                    {!form.kunde_id ? <div className={`flex items-center h-[38px] px-3 rounded border text-sm ${"bg-white/[0.02] border-white/10 text-white/30"}`}>
                        Zuerst Kunde auswählen
                      </div> : <ResponsiveSelect inputId={fieldId("objekt-id")} options={objekteOpts} value={objekteOpts.find(o => o.value === Number(form.objekt_id)) || null} onChange={opt => setForm(s => ({
                    ...s,
                    objekt_id: opt ? String(opt.value) : ""
                  }))} placeholder="Objekt auswählen…" isClearable isSearchable styles={rsStyles} menuPortalTarget={menuPortalTarget} />}
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor={fieldId("typ")}>Typ *</Label>
                <ResponsiveSelect inputId={fieldId("typ")} options={typOpts} value={typOpts.find(o => o.value === form.typ) || null} onChange={opt => setForm(s => ({
                ...s,
                typ: opt?.value || s.typ,
                agb_version_id: ""
              }))} isSearchable={false} styles={rsStyles} menuPortalTarget={menuPortalTarget} />
              </div>
              <div />

              <div className={`md:col-span-2 rounded-lg border p-3 space-y-3 ${"border-white/10 bg-white/[0.02]"}`}>
                <div className="text-xs uppercase tracking-wide opacity-80 mb-2">Verantwortung / Rechnungsflow</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={fieldId("projektleiter")}>Projektleiter</Label>
                    <ResponsiveSelect inputId={fieldId("projektleiter")} options={userOpts} value={userOpts.find(o => o.value === Number(form.projektleiter_user_id)) || null} onChange={opt => setForm(s => ({
                    ...s,
                    projektleiter_user_id: opt ? String(opt.value) : ""
                  }))} placeholder="Projektleiter auswählen…" isClearable isSearchable styles={rsStyles} menuPortalTarget={menuPortalTarget} />
                  </div>
                  <div>
                    <Label htmlFor={fieldId("auftraggeber")}>
                      Auftraggeber intern <span className={"text-blue-300/50 text-[10px]"}>(Info für Rückfragen)</span>
                    </Label>
                    <ResponsiveSelect inputId={fieldId("auftraggeber")} options={userOpts} value={userOpts.find(o => o.value === Number(form.auftraggeber_user_id)) || null} onChange={opt => setForm(s => ({
                    ...s,
                    auftraggeber_user_id: opt ? String(opt.value) : ""
                  }))} placeholder="Auftraggeber auswählen…" isClearable isSearchable styles={rsStyles} menuPortalTarget={menuPortalTarget} />
                  </div>
                  <div className="text-sm">
                    <div className="mb-1">Rechnung durch</div>
                    <div className="flex gap-2">
                      {["BUERO", "PL"].map(v => <AppButton key={v} type="button" variant="ghost" onClick={() => setForm(s => ({
                      ...s,
                      rechnung_owner_type: v,
                      rechnung_owner_user_id: v === "BUERO" ? "" : s.rechnung_owner_user_id,
                    }))} className={`px-3 py-1.5 rounded-lg text-xs border transition ${form.rechnung_owner_type === v ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"}`}>
                          {v === "BUERO" ? "Büro" : "Projektleiter"}
                        </AppButton>)}
                    </div>
                    {form.rechnung_owner_type === "BUERO" ? (
                      <p className="mt-1.5 text-[11px] text-blue-300/60 leading-snug">
                        Beim Rechnungslauf werden alle Büro-Mitarbeitenden automatisch zugewiesen.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DateOnlyField id={fieldId("vertragsbeginn")} label="Vertragsbeginn *" value={form.vertragsbeginn} onChange={v => setForm(s => ({
                ...s,
                vertragsbeginn: v
              }))} />
                <DateOnlyField id={fieldId("kuendigungstermin")} label="Kündigungstermin" value={form.kuendigungstermin} onChange={v => setForm(s => ({
                ...s,
                kuendigungstermin: v
              }))} />
              </div>

              <ModuleToggle availableModules={availableModules} enabledModules={form.enabled_modules} onChange={setEnabledModules} disabled={saving || loadingEdit} />

              {(Array.isArray(form.enabled_modules) ? form.enabled_modules : []).filter(moduleId => availableModules.includes(moduleId)).map(moduleId => <ModuleBlock key={moduleId} moduleId={moduleId} entityType="service" values={moduleValues[moduleId] || {}} onChange={(fieldName, value) => onModuleFieldChange(moduleId, fieldName, value)} collapsed />)}

              {touched && (!form.kunde_id || !form.objekt_id || !form.typ || !form.vertragsbeginn) ? <div role="alert" aria-live="assertive" className={`md:col-span-2 ${"text-rose-300 text-sm"}`}>
                  Bitte Pflichtfelder ausfüllen: Kunde, Objekt/Liegenschaft, Typ und Vertragsbeginn.
                </div> : null}

              <div className="md:col-span-2 hidden md:flex flex-wrap justify-end gap-2 pt-2 items-center">
                <AppButton type="button" variant="secondary" size="sm" className={`shrink-0 ${detailPageHeaderNavSecondaryShellClassName}`} onClick={() => navigate(isEditMode ? `/wartungsvertraege/${vertragId}` : "/wartungsvertraege")}>
                  Abbrechen
                </AppButton>
                <AppButton type="submit" variant="success" size="sm" className={`shrink-0 shadow-sm ${saving || loadingEdit ? successButtonLoadingClassName : ""} disabled:opacity-60`} disabled={saving || loadingEdit}>
                  {saving ? "Speichere..." : isEditMode ? "Änderungen speichern" : "Wartungsvertrag erstellen"}
                </AppButton>
              </div>

              <div className="md:col-span-2 h-20 md:h-0" />

              <div className="fixed md:hidden inset-x-0 bottom-0 z-40 px-3">
                <div className={`pointer-events-auto backdrop-blur rounded-2xl shadow-lg p-2 mb-3 border transition-all ${"bg-black/40 border-white/10"}`} style={{
                paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))"
              }}>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <AppButton type="button" variant="secondary" size="sm" onClick={() => navigate(isEditMode ? `/wartungsvertraege/${vertragId}` : "/wartungsvertraege")} className={`shrink-0 active:scale-[0.98] transition ${detailPageHeaderNavSecondaryShellClassName} !border-white/15 !bg-white/10 hover:!bg-white/15 !text-white`}>
                      Abbrechen
                    </AppButton>
                    <AppButton type="submit" variant="success" size="sm" disabled={saving || loadingEdit} className={`shrink-0 active:scale-[0.98] transition shadow-sm ${saving || loadingEdit ? successButtonLoadingClassName : ""}`}>
                      {saving ? "Speichere..." : isEditMode ? "Speichern" : "Erstellen"}
                    </AppButton>
                  </div>
                </div>
              </div>
            </form>
          </section>
          <div className="h-4 md:h-6" />
        </div>
      </div>
    </div>;
}